import { Box, Typography, useTheme } from "@mui/material";
import {
  useDrawerContext,
  useGuardedUpdater,
  useSnackbarContext,
  useCloudContext,
  useConfigContext,
} from "@WahooFitness/wahoo-offline-mfe";
import MentalRecoveryAcceptanceDrawer from "./MentalRecoveryAcceptanceDrawer";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MenuList,
  BlueSwitch,
  FancyIcon,
  MenuListItemVariant,
  WfIconWorkoutTypesGenericMentalTraining,
  WfIconContentHeadBrainFill,
} from "@WahooFitness/redesignr";
import { t } from "@lingui/macro";
import { useWsmPlugin } from "@/hooks/useWsmPlugin";
import { KCloudMentalAssessment } from "@WahooFitness/wsm-native/dist/esm/types/kc/KCloudMentalAssessment";
import { LocalEntityType } from "@WahooFitness/wsm-native/dist/esm/types/local_entity_type";
import Bugsnag from "@bugsnag/js";
import { DisplayPreferences } from "@WahooFitness/cloud-client-ts";
import {
  DisplayMentalCapacityAssessmentDailyCheckInPreference,
  DisplayPreferenceInputType,
} from "@WahooFitness/cloud-client-types";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";
import useSWR from "swr";

const useMentalRecoveryPreferences = () => {
  const { palette } = useTheme();
  const { logEvent } = useLogEvent();
  const { setDrawer, handleClose: handleCloseDrawer } = useDrawerContext();
  const { update } = useGuardedUpdater();
  const { sensorManagerPlugin } = useWsmPlugin();
  const { getCloudClient } = useCloudContext();
  const { wahooToken } = useConfigContext();
  const displayPreferences = useMemo(() => getCloudClient(DisplayPreferences), [getCloudClient]);
  const [userMentalRecoveryPreference, setUserMentalRecoveryPreference] = useState<string>("0");
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbarContext();
  // Function to initialize or fetch mental recovery preferences
  const [mentalAssessmentEntity, setMentalAssessmentEntity] =
    useState<KCloudMentalAssessment | null>(null);

  const { data: displayPreferenceData, mutate: mutateDisplayPreferences } = useSWR(
    !!wahooToken && ["getDisplayPreferences", wahooToken],
    ([_key, token]) => displayPreferences.get(token),
    { dedupingInterval: 10000 }
  );

  const initializeMentalRecoveryPreferences = useCallback(async () => {
    try {
      // First try to get existing entity
      const response = await sensorManagerPlugin.getLocalEntities({
        req: {
          entityType: LocalEntityType.MentalAssessment,
          includeDeleted: false,
        },
      });

      if (response.rsp?.mental_assessments && response.rsp.mental_assessments.length > 0) {
        // Entity exists, use its value
        const mentalAssessment = response.rsp.mental_assessments[0] as KCloudMentalAssessment;
        setMentalAssessmentEntity(mentalAssessment);
        setUserMentalRecoveryPreference(mentalAssessment.medical_warning_response ? "1" : "0");
        return mentalAssessment;
      } else {
        // Entity doesn't exist, create a new one
        const createResult = await sensorManagerPlugin.createLocalEntity({
          req: {
            mental_assessment: {
              medical_warning_response: false, // default value
              show_warning: true, // default value
            },
          },
        });

        const newAssessmentEntity = createResult.rsp?.mental_assessment || null;
        setMentalAssessmentEntity(newAssessmentEntity);
        setUserMentalRecoveryPreference(newAssessmentEntity?.medical_warning_response ? "1" : "0");
        return newAssessmentEntity;
      }
    } catch (error) {
      console.error("Error initializing mental recovery preferences:", error);
      return null;
    }
  }, [sensorManagerPlugin]);

  // Function to update mental recovery preferences
  const updateMentalRecoveryPreference = useCallback(
    async (medicalWarningResponseAccepted: boolean) => {
      return update(
        async () => {
          if (mentalAssessmentEntity && mentalAssessmentEntity.token) {
            const { completed } = await sensorManagerPlugin.editLocalEntity({
              entityType: LocalEntityType.MentalAssessment,
              token: mentalAssessmentEntity.token,
              edits: {
                medical_warning_response: medicalWarningResponseAccepted,
              },
            });
            if (!completed) {
              throw new Error("Failed to update mental recovery preference.");
            }
            return completed;
          }
        },
        {
          errorMessage: t`Failed to update mental recovery preference. Please try again.`,
        }
      );
    },
    [update, mentalAssessmentEntity, sensorManagerPlugin]
  );

  const setLocalUserMentalRecoveryPreferences = useCallback(
    async (value: string) => {
      try {
        setIsLoading(true);
        await updateMentalRecoveryPreference(value === "1");
        setMentalAssessmentEntity((prev) => ({
          ...prev,
          medical_warning_response: value === "1",
        }));
        setUserMentalRecoveryPreference(value);
        setIsLoading(false);
        handleCloseDrawer();
      } catch (error) {
        // if we just want to eat the error
        if (error instanceof Error) {
          Bugsnag.notify(error);
        }
        setIsLoading(false);
        enqueueSnackbar({
          message: t`Failed to update mental recovery preference. Please try again.`,
          severity: "error",
        });
        handleCloseDrawer();
      }
    },
    [updateMentalRecoveryPreference, handleCloseDrawer, enqueueSnackbar]
  );

  // Load preferences on component mount
  useEffect(() => {
    initializeMentalRecoveryPreferences();
  }, [initializeMentalRecoveryPreferences]);

  const showMentalRecoveryAcceptanceDrawer = useCallback(() => {
    setDrawer({
      open: true,
      hidePuller: true,
      hideClose: true,
      hideDivider: true,
      hideHeader: true,
      children: (
        <MentalRecoveryAcceptanceDrawer
          isSavingPreference={isLoading}
          onAccept={() => {
            setLocalUserMentalRecoveryPreferences("1");
          }}
          onCancel={handleCloseDrawer}
        />
      ),
    });
  }, [handleCloseDrawer, isLoading, setDrawer, setLocalUserMentalRecoveryPreferences]);

  const toggleEnableBinauralBeats = useCallback(async () => {
    if (userMentalRecoveryPreference === "1") {
      // the toggle is currently on, so we need to turn it off
      setUserMentalRecoveryPreference("0");
      try {
        await updateMentalRecoveryPreference(false);
        setMentalAssessmentEntity((prev) => ({
          ...prev,
          medical_warning_response: false,
        }));
      } catch (error) {
        if (error instanceof Error) {
          Bugsnag.notify(error);
        }
        enqueueSnackbar({
          message: t`Failed to disable binaural beats & subliminal priming. Please try again.`,
          severity: "error",
        });
        setLocalUserMentalRecoveryPreferences("1");
      }
    } else {
      showMentalRecoveryAcceptanceDrawer();
    }
  }, [
    userMentalRecoveryPreference,
    updateMentalRecoveryPreference,
    enqueueSnackbar,
    setLocalUserMentalRecoveryPreferences,
    showMentalRecoveryAcceptanceDrawer,
  ]);

  const wahoosMentalRecoverySessions = "Wahoo's Mental Recovery Sessions";

  const titleItem = useMemo(() => {
    const MentalRecoveryDescription = () => (
      <Box display="flex" flexDirection="column" textAlign="center" gap={1}>
        <Typography variant="prose-md-medium" color={palette.text.primary}>
          {t`Binaural beats & Subliminal Priming`}
        </Typography>
        <Typography variant="prose-base" color={palette.text.secondary}>
          {t`Binaural beats uses inaudible tones integrated within the background music to help
              improve mental focus and relaxation. Subliminal priming uses subtle background imagery
              not discernible to the human eye to help improve mood and mindset. Found in some of
              ${wahoosMentalRecoverySessions}, these aim to enhance the workout & recovery
              experience.`}
        </Typography>
      </Box>
    );

    return {
      id: "mental-recovery-title",
      content: <MentalRecoveryDescription />,
      variant: MenuListItemVariant.NoAction,
    };
  }, [palette.text.primary, palette.text.secondary]);

  const binauralBeatsListItem = useMemo(
    () => ({
      id: "mental-recovery-binaural-beats",
      content: t`Enable Binaural beats & Subliminal Priming`,
      variant: MenuListItemVariant.Action,
      actionComponent: <BlueSwitch checked={userMentalRecoveryPreference === "1"} />,
      hasDivider: false,
      action: toggleEnableBinauralBeats,
    }),
    [toggleEnableBinauralBeats, userMentalRecoveryPreference]
  );

  const binuralBeatsIconItem = useMemo(
    () => ({
      id: "mental-recovery-icon",
      content: (
        <Box display="flex" flexDirection="column" alignItems="center" mt={1} mb={1}>
          <FancyIcon
            icon={<WfIconWorkoutTypesGenericMentalTraining />}
            color="info"
            size={"medium"}
          />
        </Box>
      ),
      variant: MenuListItemVariant.NoAction,
      hasDivider: false,
    }),
    []
  );

  const updateDisplayPreferences = useCallback(
    async (update: DisplayPreferenceInputType) => {
      try {
        await displayPreferences.update(wahooToken, update);
      } catch (error) {
        const sectionName = t`Daily Mental Check-in`;
        enqueueSnackbar({
          message: t`An error occurred when saving ${sectionName}. Please check your internet connection and try again.`,
          severity: "error",
        });
      }
    },
    [displayPreferences, enqueueSnackbar, wahooToken]
  );

  const toggleDailyMentalCheckIn = useCallback(async () => {
    const newValue =
      displayPreferenceData?.mental_capacity_assessments ===
      DisplayMentalCapacityAssessmentDailyCheckInPreference.always
        ? DisplayMentalCapacityAssessmentDailyCheckInPreference.never
        : DisplayMentalCapacityAssessmentDailyCheckInPreference.always;
    if (newValue) {
      logEvent("mental_assessment", {
        click_target: ClickTarget.daily_mental_check_in_enabled,
        event_type: EventType.click,
        source_page_name: SourcePage.mental_training_preferences,
      });
    } else {
      logEvent("mental_assessment", {
        click_target: ClickTarget.daily_mental_check_in_disabled,
        event_type: EventType.click,
        source_page_name: SourcePage.mental_training_preferences,
      });
    }
    await updateDisplayPreferences({
      mental_capacity_assessments: newValue
        ? DisplayMentalCapacityAssessmentDailyCheckInPreference.always
        : DisplayMentalCapacityAssessmentDailyCheckInPreference.never,
    });
    // I'm not sure if the mutate function already merges updates in the mutate call or if it simply replaces the object
    mutateDisplayPreferences(
      Object.assign({}, displayPreferenceData, {
        mental_capacity_assessments: newValue,
      })
    );
  }, [logEvent, updateDisplayPreferences, mutateDisplayPreferences, displayPreferenceData]);

  const dailyMentalCheckInIconItem = useMemo(
    () => ({
      id: "daily-mental-check-in-icon",
      content: (
        <Box display="flex" flexDirection="column" alignItems="center" mt={1} mb={1}>
          <FancyIcon icon={<WfIconContentHeadBrainFill />} color="info" size={"medium"} />
        </Box>
      ),
      variant: MenuListItemVariant.NoAction,
      hasDivider: false,
    }),
    []
  );

  const dailyMentalCheckInTitleItem = useMemo(() => {
    const DailyMentalCheckInDescription = () => (
      <Box display="flex" flexDirection="column" textAlign="center" gap={1}>
        <Typography variant="prose-md-medium" color={palette.text.primary}>
          {t`Daily mental check-in`}
        </Typography>
        <Typography variant="prose-base" color={palette.text.secondary}>
          {t`Daily Mental Capacity assessments help Wahoo to better understand your mental state, training capacity, and recovery needs.`}
        </Typography>
      </Box>
    );

    return {
      id: "daily-mental-check-in-title",
      content: <DailyMentalCheckInDescription />,
      variant: MenuListItemVariant.NoAction,
    };
  }, [palette.text.primary, palette.text.secondary]);

  const dailyMentalCheckInToggleItem = useMemo(
    () => ({
      id: "daily-mental-check-in-toggle",
      content: t`Enable`,
      variant: MenuListItemVariant.Action,
      actionComponent: (
        <BlueSwitch
          checked={
            displayPreferenceData?.mental_capacity_assessments ===
            DisplayMentalCapacityAssessmentDailyCheckInPreference.always
          }
        />
      ),
      hasDivider: false,
      action: toggleDailyMentalCheckIn,
    }),
    [toggleDailyMentalCheckIn, displayPreferenceData]
  );

  const MentalRecoveryMedicalWarningContent = () => {
    return (
      <Box mx={2} display="flex" flexDirection="column">
        <MenuList
          listItems={[binuralBeatsIconItem, titleItem, binauralBeatsListItem]}
          disableGutters
        />
      </Box>
    );
  };

  const DailyMentalCheckInContent = () => {
    return (
      <Box mx={2} display="flex" flexDirection="column">
        <MenuList
          listItems={[
            dailyMentalCheckInIconItem,
            dailyMentalCheckInTitleItem,
            dailyMentalCheckInToggleItem,
          ]}
          disableGutters
        />
      </Box>
    );
  };

  return {
    MentalRecoveryMedicalWarningContent,
    DailyMentalCheckInContent,
  };
};

export { useMentalRecoveryPreferences };
